import { useEffect, useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import U2000SaveCancelDialog from '../../../../u2000Core/components/dialog/U2000SaveCancelDialog';
import {
  U3203_DEFAULT_PROMPT_LABEL_MAX,
  U3203_MAX_ACTIVE_DEFAULT_PROMPTS,
  u3203CountActiveDefaultPrompts,
  type U3203DefaultPrompt,
} from '../../../U3203Service';
import { NS } from '../../../U3203';

interface U3203AiMissionDefaultPromptDlgProps {
  isOpen: boolean;
  mode: 'add' | 'edit';
  draft: U3203DefaultPrompt | null;
  existingItems: U3203DefaultPrompt[];
  t: TFunction;
  onClose: () => void;
  onSave: (item: U3203DefaultPrompt) => void;
}

function U3203AiMissionDefaultPromptDlg({
  isOpen,
  mode,
  draft,
  existingItems,
  t,
  onClose,
  onSave,
}: U3203AiMissionDefaultPromptDlgProps) {
  const [labelFr, setLabelFr] = useState('');
  const [labelEn, setLabelEn] = useState('');
  const [promptFr, setPromptFr] = useState('');
  const [promptEn, setPromptEn] = useState('');
  const [sortOrder, setSortOrder] = useState('1');
  const [active, setActive] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !draft) return;
    setLabelFr(draft.labelFr);
    setLabelEn(draft.labelEn);
    setPromptFr(draft.promptFr ?? '');
    setPromptEn(draft.promptEn ?? '');
    setSortOrder(String(draft.sortOrder));
    setActive(draft.active);
    setError(null);
  }, [isOpen, draft]);

  const isValid = useMemo(() => {
    const order = Number(sortOrder);
    return (
      labelFr.trim().length > 0 &&
      labelEn.trim().length > 0 &&
      promptFr.trim().length > 0 &&
      promptEn.trim().length > 0 &&
      Number.isInteger(order) &&
      order >= 1
    );
  }, [labelEn, labelFr, promptEn, promptFr, sortOrder]);

  const missingRequiredHint = useMemo(() => {
    if (isValid) return null;
    if (!labelFr.trim() || !labelEn.trim() || !promptFr.trim() || !promptEn.trim()) {
      return t('errDefaultPromptRequired', { ns: NS });
    }
    return t('errDefaultPromptSortOrder', { ns: NS });
  }, [isValid, labelEn, labelFr, promptEn, promptFr, t]);

  const handleClose = (action: 'save' | 'cancel') => {
    if (action !== 'save' || !draft) {
      onClose();
      return;
    }

    const order = Number(sortOrder);
    const next: U3203DefaultPrompt = {
      ...draft,
      labelFr: labelFr.trim(),
      labelEn: labelEn.trim(),
      promptFr: promptFr.trim(),
      promptEn: promptEn.trim(),
      sortOrder: order,
      active,
    };

    if (
      next.labelFr.length > U3203_DEFAULT_PROMPT_LABEL_MAX ||
      next.labelEn.length > U3203_DEFAULT_PROMPT_LABEL_MAX
    ) {
      setError(t('errDefaultPromptLabelMax', { ns: NS, max: U3203_DEFAULT_PROMPT_LABEL_MAX }));
      return;
    }

    const duplicateOrder = existingItems.some(
      (item) => item.id !== draft.id && item.sortOrder === next.sortOrder,
    );
    if (duplicateOrder) {
      setError(t('errDefaultPromptSortOrderUnique', { ns: NS }));
      return;
    }

    if (next.active) {
      const otherActives = u3203CountActiveDefaultPrompts(existingItems, draft.id);
      if (otherActives >= U3203_MAX_ACTIVE_DEFAULT_PROMPTS) {
        setError(t('errDefaultPromptLimit', { ns: NS }));
        return;
      }
    }

    onSave(next);
    onClose();
  };

  if (!isOpen || !draft) return null;

  return (
    <U2000SaveCancelDialog
      isOpen={isOpen}
      onClose={handleClose}
      title={t(mode === 'add' ? 'dlgDefaultPromptAdd' : 'dlgDefaultPromptEdit', { ns: NS })}
      width="large"
      isValid={isValid}
    >
      <div className="flex flex-col gap-y-3 p-1">
        {error ? <p className="text-sm text-red-600">{error}</p> : null}
        {missingRequiredHint ? <p className="text-sm text-amber-700">{missingRequiredHint}</p> : null}
        <label className="flex flex-col gap-y-1 text-sm">
          <span className="font-medium text-gray-700">{t('lblLabelFr', { ns: NS })}</span>
          <input
            className="rounded-sm border border-gray-300 px-3 py-2 text-sm"
            value={labelFr}
            maxLength={U3203_DEFAULT_PROMPT_LABEL_MAX}
            onChange={(event) => setLabelFr(event.target.value)}
          />
        </label>
        <label className="flex flex-col gap-y-1 text-sm">
          <span className="font-medium text-gray-700">{t('lblLabelEn', { ns: NS })}</span>
          <input
            className="rounded-sm border border-gray-300 px-3 py-2 text-sm"
            value={labelEn}
            maxLength={U3203_DEFAULT_PROMPT_LABEL_MAX}
            onChange={(event) => setLabelEn(event.target.value)}
          />
        </label>
        <label className="flex flex-col gap-y-1 text-sm">
          <span className="font-medium text-gray-700">{t('lblPromptFr', { ns: NS })}</span>
          <textarea
            className="rounded-sm border border-gray-300 px-3 py-2 text-sm"
            rows={3}
            value={promptFr}
            onChange={(event) => setPromptFr(event.target.value)}
          />
        </label>
        <label className="flex flex-col gap-y-1 text-sm">
          <span className="font-medium text-gray-700">{t('lblPromptEn', { ns: NS })}</span>
          <textarea
            className="rounded-sm border border-gray-300 px-3 py-2 text-sm"
            rows={3}
            value={promptEn}
            required
            onChange={(event) => setPromptEn(event.target.value)}
          />
        </label>
        <label className="flex flex-col gap-y-1 text-sm">
          <span className="font-medium text-gray-700">{t('lblSortOrder', { ns: NS })}</span>
          <input
            type="number"
            min={1}
            className="w-32 rounded-sm border border-gray-300 px-3 py-2 text-sm"
            value={sortOrder}
            onChange={(event) => setSortOrder(event.target.value)}
          />
        </label>
        <label className="flex items-center gap-x-2 text-sm">
          <input
            type="checkbox"
            checked={active}
            onChange={(event) => setActive(event.target.checked)}
          />
          <span className="font-medium text-gray-700">{t('lblActive', { ns: NS })}</span>
        </label>
      </div>
    </U2000SaveCancelDialog>
  );
}

export default U3203AiMissionDefaultPromptDlg;
